import { VisitSummary } from "@cur8/rich-entity";
import { Flags, FLAGS } from "lib/flags";
import { lastAuditChangeDate } from "lib/lastAuditChangeDate";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { Separator } from "render/ui/presentation/Separator";
import { Typography } from "render/ui/presentation/Typography/Typography";
import { Summary } from "render/ui/presentation/doctor/Summary";
import { PostScanShareViewEntrypoint } from "render/views/PostScanShareView/components/PostScanShareViewEntrypoint";
import { NextScanRecomendation } from "./components/NextScanRecomendation/NextScanRecomendation";
import { PreviousSummaries } from "./components/PreviousSummaries";
import styles from "./styles.module.sass";
import { Trans } from "./trans";

interface SummariesTabProps {
  visitsSummaries: VisitSummary[] | undefined;
  latestScanDate: DateTime;
  onGoToScanData: () => void;
}

export function SummariesTab({
  visitsSummaries,
  latestScanDate,
  onGoToScanData,
}: SummariesTabProps) {
  const hasShareViewCardFeatureFlag = FLAGS.has(Flags.ShareViewCard);

  const latestSummary = useMemo(() => {
    if (visitsSummaries == null) {
      return undefined;
    }
    return visitsSummaries.find(
      (summary) =>
        summary.visitDate.startOf("day") >= latestScanDate.startOf("day")
    );
  }, [visitsSummaries, latestScanDate]);

  const previousSummaries = useMemo(() => {
    if (!latestSummary || !visitsSummaries) {
      return visitsSummaries ?? [];
    }
    return visitsSummaries.filter((summary) => summary.id !== latestSummary.id);
  }, [visitsSummaries, latestSummary]);

  if (!visitsSummaries) {
    return null;
  }

  return (
    <div className={styles.SummariesTab} data-testid="summaries-tab">
      <div className={styles.header}>
        <div className={styles.titles}>
          <Typography variant="display-s">
            <Trans.Title />
          </Typography>
          {latestSummary && (
            <div className={styles.subline}>
              <Typography variant="body-xs" whiteSpace="pre" color="subtle">
                <Trans.WrittenOn />
              </Typography>
              <Typography variant="body-xs" color="default">
                {lastAuditChangeDate(latestSummary.audit)?.toFormat(
                  "dd MMMM yyyy"
                )}
              </Typography>
            </div>
          )}
        </div>
        <div className={styles.actions}>
          {hasShareViewCardFeatureFlag && <PostScanShareViewEntrypoint />}
        </div>
      </div>
      <Separator width="24px" />
      <div className={styles.texts}>
        {!latestSummary && (
          <span className={styles.summaryIsBeingWritten}>
            <Trans.FollowUpBeingPrepared onClick={onGoToScanData} />
          </span>
        )}
        {latestSummary && latestSummary?.followUpDate && (
          <>
            <div className={styles.latestSummary}>
              <Summary
                text={latestSummary.summaryText}
                doctorURI={latestSummary.audit.created?.userUri?.toString()}
                onClick={onGoToScanData}
              />
            </div>
            <NextScanRecomendation
              scanDate={latestSummary.visitDate}
              followUpDate={latestSummary.followUpDate}
            />
          </>
        )}

        {previousSummaries.length > 0 && (
          <PreviousSummaries visitsSummaries={previousSummaries} />
        )}
      </div>
    </div>
  );
}
